import { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { StagedPhotoDTO } from '@/types/create/StagedPhotoDTO';
import { getLetterSent, getStagedPhotos } from '@/services/createFormSlice';
import { letterApiSlice } from '@/services/api/letterApiSlice';
import type { AppDispatch, RootState } from '@/app/store';

/**
 * Drops from the server every staged photo the create form stops holding, whichever action let go of it.
 * <p>
 * The form holds the only identifiers that address its staged photos, and a sender may hold only so many at once,
 * so a photo the form forgets without dropping stays counted against that allowance until the sweep removes it.
 * Watching the form rather than the actions is what keeps the next action that empties it from forgetting too.
 * Nothing is dropped once the letter is sent: creating it has already moved its photos out of staging.
 */
export const stagedPhotoDropMiddleware: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  const before: StagedPhotoDTO[] = getStagedPhotos(api.getState() as RootState);
  const isLetterSent: boolean = getLetterSent(api.getState() as RootState);
  const result: unknown = next(action);
  const after: StagedPhotoDTO[] = getStagedPhotos(api.getState() as RootState);
  if (after === before || isLetterSent) {
    return result;
  }
  const kept: Set<string> = new Set(after.map((photo: StagedPhotoDTO): string => photo.id));
  const dispatch: AppDispatch = api.dispatch as AppDispatch;
  for (const photo of before) {
    if (!kept.has(photo.id)) {
      // A drop the server refuses is swept once its lifetime is over, so nothing waits on the answer here.
      void dispatch(letterApiSlice.endpoints.deleteStagedPhoto.initiate(photo.id, { track: false }));
    }
  }
  return result;
};
